import Phaser from 'phaser';
import { EntityCategory, Allegiance } from '../constants/enums';

export { EntityCategory, Allegiance };

/**
 * SpawnFlags - Controls culling and despawn behavior per prefab
 */
export interface SpawnFlags {
  /** If true, entity CAN be destroyed when outside despawn zone (default: false) */
  canDespawn?: boolean;
  /** If true, entity CAN be deactivated when outside cull zone (default: true) */
  canCull?: boolean;
  /** If true, entity is ALWAYS active, ignoring culling (default: false) */
  alwaysActive?: boolean;
}

export interface PrefabData {
  category: EntityCategory;
  class?: string;
  texture: string;
  hp?: number;
  maxHp?: number;
  allegiance?: Allegiance;
  behaviors?: Record<string, unknown>;
  phases?: Array<{
    hpPercent: number;
    pattern: string;
    dropOnEnter?: string;
  }>;
  spawnFlags?: SpawnFlags;
  detectionRange?: number;
  [key: string]: unknown;
}

export interface PrefabsFile {
  _meta?: {
    warning: string;
    source: string;
    generatedAt: string;
  };
  entities: Record<string, PrefabData>;
}

export interface InstanceData {
  entityId: string;
  x: number;
  y: number;
  id?: string;
  texture?: string;
  properties?: Record<string, unknown>;
  override?: Partial<PrefabData>;
  [key: string]: unknown;  // Allow additional properties (width, height, originX, originY, etc.)
}

export interface ResolvedEntityData extends PrefabData {
  type: string;
  x: number;
  y: number;
  id?: string;
  // Animation initial state (from animations-overrides.json)
  initialAnimation?: string;
  initialFrame?: number;
  autoPlay?: boolean;
}

// =============================================================================
// Manual Overrides (visual/position adjustments, loaded at runtime)
// =============================================================================

interface ManualOverridesData {
  entities?: Record<string, Partial<PrefabData>>;
  levels?: Record<string, {
    entities?: {
      excludeFromLayer?: Record<string, string[]>;
    };
  }>;
}


/**
 * DataManagerEntity - Simplified Runtime System
 *
 * Loads consolidated entities-c3data.json (generated at build-time) and applies
 * entities-overrides.json at runtime for visual/position adjustments.
 *
 * Build-time: C3 families + behavior-config.json → entities-c3data.json
 * Runtime: entities-c3data.json + entities-overrides.json + instance properties
 */
export class DataManagerEntity {
  private scene: Phaser.Scene;
  private prefabs: Record<string, PrefabData> = {};
  /** Maps lowercase names to original names for case-insensitive lookups */
  private nameMap: Map<string, string> = new Map();
  private manualOverrides: ManualOverridesData | null = null;
  private currentLevel: string | null = null;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * Normalize a prefab name to find the canonical name (case-insensitive)
   * @returns The original prefab name or undefined if not found
   */
  private normalizeName(name: string): string | undefined {
    if (!name) {
      return undefined;
    }
    // Try exact match first
    if (this.prefabs[name]) {
      return name;
    }
    // Try case-insensitive lookup
    return this.nameMap.get(name.toLowerCase());
  }

  /**
   * Set the current level for level-specific overrides (excludeFromLayer, etc.)
   */
  setCurrentLevel(levelId: string): void {
    this.currentLevel = levelId;
  }

  /**
   * Load prefabs from JSON data
   */
  load(prefabsData: PrefabsFile): void {
    this.prefabs = prefabsData.entities;
    // Build case-insensitive name map
    this.nameMap.clear();
    for (const name of Object.keys(this.prefabs)) {
      this.nameMap.set(name.toLowerCase(), name);
    }
  }

  /**
   * Load prefabs from cache key
   */
  loadFromCache(key: string): void {
    const data = this.scene.cache.json.get(key) as PrefabsFile;
    if (data) {
      this.load(data);
    } else {
      console.warn(`DataManagerEntity: No JSON found in cache for key "${key}"`);
    }
  }

  /**
   * Load entity overrides from cache (optional)
   * Also registers new entities from overrides that don't exist in base data
   */
  loadOverrides(key: string = 'entities-overrides'): void {
    this.manualOverrides = this.scene.cache.json.get(key) as ManualOverridesData;

    // Register new entities from overrides that don't exist in base data
    if (this.manualOverrides?.entities) {
      for (const [name, data] of Object.entries(this.manualOverrides.entities)) {
        // Skip metadata keys like _comment
        if (name.startsWith('_')) continue;
        if (!this.prefabs[name]) {
          // New entity not in base - add it with required defaults
          const overrideData = data as Partial<PrefabData>;
          this.prefabs[name] = {
            category: overrideData.category || EntityCategory.Unit,
            texture: overrideData.texture || name,
            ...overrideData,
          } as PrefabData;
          this.nameMap.set(name.toLowerCase(), name);
        }
      }
    }
  }


  /**
   * Get a prefab by name (case-insensitive)
   */
  get(prefabName: string): PrefabData | undefined {
    const canonical = this.normalizeName(prefabName);
    return canonical ? this.prefabs[canonical] : undefined;
  }

  /**
   * Check if prefab exists (case-insensitive)
   */
  has(prefabName: string): boolean {
    return this.normalizeName(prefabName) !== undefined;
  }

  /**
   * Get all prefab names
   */
  getNames(): string[] {
    return Object.keys(this.prefabs);
  }

  /**
   * Get prefabs by category
   */
  getByCategory(category: PrefabData['category']): Record<string, PrefabData> {
    const result: Record<string, PrefabData> = {};
    for (const [name, prefab] of Object.entries(this.prefabs)) {
      if (prefab.category === category) {
        result[name] = prefab;
      }
    }
    return result;
  }

  /**
   * Resolve an instance: merge prefab + instance properties + overrides
   */
  resolve(instanceData: InstanceData): ResolvedEntityData | null {
    // Validate entityId exists
    if (!instanceData.entityId) {
      console.error('[DataManagerEntity] Missing entityId in instanceData:', instanceData);
      return null;
    }

    // Normalize prefab name (case-insensitive lookup)
    const canonicalName = this.normalizeName(instanceData.entityId);
    if (!canonicalName) {
      console.error(`[DataManagerEntity] Entity not found: "${instanceData.entityId}"`, instanceData);
      return null;
    }

    const prefab = this.prefabs[canonicalName];

    // Get instance properties from C3 extraction
    const c3Properties = instanceData.properties || {};

    // Extract visual/position properties from instance (width, height, originX, originY, etc.)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { entityId: _entityId, x, y, id, properties, override, ...visualProps } = instanceData;

    // Start with prefab data (use canonical name for consistency)
    const resolved: ResolvedEntityData = {
      type: canonicalName,
      ...this.deepClone(prefab),
      x: instanceData.x,
      y: instanceData.y,
      // Use explicit id if provided, otherwise use canonicalName (so entities always have an id)
      id: id ?? canonicalName,
      ...visualProps,
    };

    // Apply C3 instance properties (override prefab defaults)
    if (c3Properties.health !== undefined) {
      resolved.hp = c3Properties.health as number;
    }
    if (c3Properties.maxHealth !== undefined) {
      resolved.maxHp = c3Properties.maxHealth as number;
    } else if (c3Properties.health !== undefined) {
      resolved.maxHp = c3Properties.health as number;
    }

    // Apply C3 properties to behaviors
    this.applyC3PropertiesToBehaviors(resolved, c3Properties);

    // Add instance ID
    if (id) {
      resolved.id = id;
    }

    // Apply manual overrides for this prefab type (use canonical name)
    if (this.manualOverrides?.entities?.[canonicalName]) {
      const entityOverride = this.manualOverrides.entities[canonicalName];
      this.deepMerge(resolved, entityOverride as Partial<ResolvedEntityData>);
    }

    // Apply instance-specific override (from level data)
    if (override) {
      this.deepMerge(resolved, override as Partial<ResolvedEntityData>);
    }

    // Apply animation initial state from entity's animations config (in entities-overrides.json)
    const entityOverrideForAnim = this.manualOverrides?.entities?.[canonicalName];
    if (entityOverrideForAnim) {
      const animConfig = (entityOverrideForAnim as Record<string, unknown>).animations as {
        initial?: string;
        initialFrame?: number;
        autoPlay?: boolean;
      } | undefined;
      if (animConfig) {
        if (animConfig.initial) {
          resolved.initialAnimation = animConfig.initial;
        }
        if (animConfig.initialFrame !== undefined) {
          resolved.initialFrame = animConfig.initialFrame;
        }
        if (animConfig.autoPlay !== undefined) {
          resolved.autoPlay = animConfig.autoPlay;
        }
      }
    }

    return resolved;
  }

  /**
   * Apply C3 instance properties to behavior configs
   */
  private applyC3PropertiesToBehaviors(
    resolved: ResolvedEntityData,
    c3Properties: Record<string, unknown>
  ): void {
    const behaviors = resolved.behaviors || {};

    // Speed → walker/flyer
    if (c3Properties.speed !== undefined) {
      if (behaviors.walker) {
        (behaviors.walker as Record<string, unknown>).speed = c3Properties.speed;
      }
      if (behaviors.flyer) {
        (behaviors.flyer as Record<string, unknown>).speed = c3Properties.speed;
      }
    }

    // Biomoney/weight → abductable
    if (behaviors.abductable) {
      const abductable = behaviors.abductable as Record<string, unknown>;
      if (c3Properties.biomoney !== undefined) {
        abductable.bioMatter = c3Properties.biomoney;
      }
      if (c3Properties.weight !== undefined) {
        const weight = c3Properties.weight as number;
        if (weight <= 10) abductable.weight = 'light';
        else if (weight <= 20) abductable.weight = 'medium';
        else abductable.weight = 'heavy';
      }
    }

    // Range/rateOfFire/turnable → shooter + detectionRange
    if (behaviors.shooter) {
      const shooter = behaviors.shooter as Record<string, unknown>;
      if (c3Properties.rateOfFire !== undefined) {
        // C3 uses seconds, convert to ms
        shooter.fireRate = (c3Properties.rateOfFire as number) * 1000;
      }
      if (c3Properties.turnable !== undefined) {
        shooter.turnable = c3Properties.turnable;
      }
    }
    if (c3Properties.range !== undefined) {
      resolved.detectionRange = c3Properties.range as number;
    }

    resolved.behaviors = behaviors;
  }

  /**
   * Resolve multiple instances
   */
  resolveMany(instances: InstanceData[]): ResolvedEntityData[] {
    const resolved: ResolvedEntityData[] = [];

    for (const instance of instances) {
      const data = this.resolve(instance);
      if (data) {
        resolved.push(data);
      }
    }

    return resolved;
  }

  /**
   * Deep clone an object
   */
  private deepClone<T>(obj: T): T {
    return JSON.parse(JSON.stringify(obj));
  }

  /**
   * Deep merge objects
   */
  private deepMerge<T extends Record<string, unknown>>(target: T, ...sources: Partial<T>[]): T {
    for (const source of sources) {
      for (const key in source) {
        const sourceValue = source[key];
        const targetValue = target[key];

        if (
          sourceValue &&
          typeof sourceValue === 'object' &&
          !Array.isArray(sourceValue)
        ) {
          target[key] = this.deepMerge(
            (targetValue as Record<string, unknown>) || {},
            sourceValue as Record<string, unknown>
          ) as T[Extract<keyof T, string>];
        } else if (sourceValue !== undefined) {
          target[key] = sourceValue as T[Extract<keyof T, string>];
        }
      }
    }
    return target;
  }

  /**
   * Clear all loaded data
   */
  clear(): void {
    this.prefabs = {};
    this.nameMap.clear();
    this.manualOverrides = null;
  }
}
